import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import HypeCircleLogo from './HypeCircleLogo';
import HypeSpinner from './HypeSpinner';

const LoadingScreen = ({ 
  message = "Loading your entertainment...",
  variant = 'splash'
}) => {
  
  if (variant === 'inline') {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          p: 4, 
          gap: 3 
        }}
      >
        <HypeCircleLogo variant="default" size="lg" />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {message}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <HypeSpinner variant="dots" size="md" />
          </Box>
        </Box>
      </Box>
    );
  }

  if (variant === 'overlay') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Glassmorphism backdrop */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backdropFilter: 'blur(12px)',
            bgcolor: (theme) => theme.palette.mode === 'dark' 
              ? 'rgba(0,0,0,0.3)' 
              : 'rgba(255,255,255,0.3)'
          }}
        />
        
        {/* Loading content */}
        <Paper
          elevation={24}
          sx={{
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            p: 4,
            borderRadius: 4,
            backdropFilter: 'blur(20px)',
            bgcolor: (theme) => theme.palette.mode === 'dark' 
              ? 'rgba(0,0,0,0.2)' 
              : 'rgba(255,255,255,0.2)',
            border: (theme) => theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.1)',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.5)'
              : '0 8px 32px rgba(0,0,0,0.1)'
          }}
        >
          <HypeCircleLogo variant="default" size="xl" />
          <Box sx={{ textAlign: 'center', gap: 2 }}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              {message}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <HypeSpinner variant="pulse" size="lg" />
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Splash screen variant
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) => theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #000000 0%, rgba(255,193,7,0.05) 50%, #000000 100%)'
          : 'linear-gradient(135deg, #f5f5f5 0%, rgba(25,118,210,0.05) 50%, #ffffff 100%)'
      }}
    >
      {/* Background pattern */}
      <Box sx={{ position: 'absolute', inset: 0, opacity: 0.3 }}>
        {[
          { top: '20%', left: '20%', size: 128, delay: '0s' },
          { top: '40%', right: '25%', size: 96, delay: '1s' },
          { bottom: '30%', left: '25%', size: 160, delay: '2s' },
          { bottom: '20%', right: '20%', size: 112, delay: '0.5s' }
        ].map((circle, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              width: circle.size,
              height: circle.size,
              borderRadius: '50%',
              background: (theme) => {
                const isDark = theme.palette.mode === 'dark';
                return isDark 
                  ? 'radial-gradient(circle, rgba(255,193,7,0.2) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(25,118,210,0.2) 0%, transparent 70%)';
              },
              filter: 'blur(40px)',
              animation: 'pulse 4s infinite',
              animationDelay: circle.delay,
              ...circle
            }}
          />
        ))}
      </Box>

      {/* Main content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          textAlign: 'center'
        }}
      >
        {/* Logo with enhanced effects */}
        <Box sx={{ position: 'relative' }}>
          <HypeCircleLogo variant="default" size="xl" />
          
          {/* Floating emotional elements */}
          {[
            { emoji: '🎬', position: { top: -16, left: -16 }, delay: '0s' },
            { emoji: '⭐', position: { top: -8, right: -24 }, delay: '0.5s' },
            { emoji: '❤️', position: { bottom: -16, left: -24 }, delay: '1s' },
            { emoji: '🔥', position: { bottom: -8, right: -16 }, delay: '1.5s' }
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                animation: 'bounce 2s infinite',
                animationDelay: item.delay,
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                  '40%': { transform: 'translateY(-8px)' },
                  '60%': { transform: 'translateY(-4px)' }
                },
                ...item.position
              }}
            >
              {item.emoji}
            </Box>
          ))}
        </Box>

        {/* Loading text */}
        <Box sx={{ gap: 3 }}>
          <Typography 
            variant="h4" 
            sx={{
              background: (theme) => {
                const isDark = theme.palette.mode === 'dark';
                return isDark 
                  ? 'linear-gradient(135deg, #ffc107 0%, #ffeb3b 50%, #ff8f00 100%)'
                  : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #1565c0 100%)';
              },
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textFillColor: 'transparent',
              fontWeight: 'bold',
              mb: 3
            }}
          >
            {message}
          </Typography>
          
          {/* Animated loading dots */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <HypeSpinner variant="dots" size="lg" />
          </Box>
        </Box>

        {/* Subtle tagline */}
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            fontSize: '0.9rem',
            letterSpacing: '0.5px'
          }}
        >
          Where entertainment meets emotion
        </Typography>
      </Box>
    </Box>
  );
};

export default LoadingScreen;